import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HandCoins } from "lucide-react";
import { API } from "@/config";

/**
 * TipSection — optional driver tip at checkout.
 *
 * UX rules (per owner spec):
 *   • Presets: Skip · 10 % · 15 % · 20 % · Other (dollar input)
 *   • Percent baseline is the RENTAL SUBTOTAL (gear × days), never delivery
 *     fee or add-ons. Server matches that math in calc_quote so the two
 *     never drift.
 *   • Tip is never taxed and never inflates Care/PoM fees.
 *   • Tip is fully refundable if the customer cancels before delivery day;
 *     non-refundable once the reservation starts.
 *
 * Wire-up:
 *   • Owns the local `mode` ("skip" | "10" | "15" | "20" | "other") and
 *     `customAmount` state.
 *   • POSTs `/checkout/update` with `tip_amount` and updates the shared
 *     quote via `setQuote`.
 */
export const TipSection = ({ reservationId, quote, setQuote }) => {
  const subtotal = Number(quote?.subtotal || 0);
  const currentTip = Number(quote?.tip_amount || 0);

  // Detect which preset matches the persisted tip so state survives refresh.
  const detectMode = () => {
    if (currentTip <= 0) return "skip";
    for (const pct of [10, 15, 20]) {
      const expected = Math.round(subtotal * (pct / 100) * 100) / 100;
      if (Math.abs(expected - currentTip) < 0.01) return String(pct);
    }
    return "other";
  };
  const [mode, setMode] = useState(detectMode);
  const [customAmount, setCustomAmount] = useState(() =>
    currentTip > 0 && detectMode() === "other" ? String(currentTip.toFixed(2)) : ""
  );
  const busy = useRef(false);

  // If the subtotal changes (dates/items edited), keep the % preset in sync
  // by re-computing the exact dollar amount server-side.
  useEffect(() => {
    if (mode === "skip" || mode === "other") return;
    const pct = Number(mode);
    const newTip = Math.round(subtotal * (pct / 100) * 100) / 100;
    if (Math.abs(newTip - currentTip) > 0.01) {
      pushTip(newTip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const pushTip = async (dollars) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const r = await axios.post(`${API}/checkout/update`, {
        reservation_id: reservationId,
        tip_amount: Number(dollars) || 0,
      });
      setQuote(r.data.quote);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update tip — please try again.");
    } finally {
      busy.current = false;
    }
  };

  const selectPreset = (nextMode) => {
    setMode(nextMode);
    if (nextMode === "skip") {
      setCustomAmount("");
      pushTip(0);
      return;
    }
    if (nextMode === "other") {
      // Wait for the user to type — don't push yet.
      return;
    }
    const pct = Number(nextMode);
    const dollars = Math.round(subtotal * (pct / 100) * 100) / 100;
    pushTip(dollars);
  };

  const onCustomBlur = () => {
    if (mode !== "other") return;
    const val = Number(customAmount);
    if (!Number.isFinite(val) || val < 0) {
      pushTip(0);
      return;
    }
    pushTip(Math.min(1000, val));
  };

  const presets = [
    { key: "skip", label: "Skip", sublabel: "No tip" },
    { key: "10", label: "10 %", sublabel: `$${(subtotal * 0.1).toFixed(2)}` },
    { key: "15", label: "15 %", sublabel: `$${(subtotal * 0.15).toFixed(2)}` },
    { key: "20", label: "20 %", sublabel: `$${(subtotal * 0.2).toFixed(2)}` },
    { key: "other", label: "Other", sublabel: "$ amount" },
  ];

  return (
    <section
      className="glass-warm p-5 sm:p-6 rounded-2xl"
      data-testid="tip-section"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/30 flex items-center justify-center shrink-0">
          <HandCoins className="w-4 h-4 text-[#FFD166]" />
        </div>
        <div>
          <div className="font-serif text-lg text-[#F6F1E4]">Add a tip ✨</div>
          <p className="text-xs text-[#B9C8D5] mt-0.5">
            Optional and 100 % goes to your driver. Never taxed. Fully refundable if you cancel before delivery day.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2 mb-3">
        {presets.map((p) => {
          const active = mode === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => selectPreset(p.key)}
              className={`rounded-lg py-2 px-1 border transition-colors text-center ${
                active
                  ? "border-[#FFD166] bg-[#FFD166]/15 text-[#FFD166]"
                  : "border-[#FFD166]/25 text-[#B9C8D5] hover:border-[#FFD166]/60"
              }`}
              data-testid={`tip-preset-${p.key}`}
            >
              <div className="text-sm font-medium leading-tight">{p.label}</div>
              <div className="text-[10px] opacity-80 leading-tight mt-0.5">{p.sublabel}</div>
            </button>
          );
        })}
      </div>

      {mode === "other" && (
        <div className="mt-2">
          <label className="eyebrow text-[10px] mb-1 block">Custom tip amount</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FFD166] text-sm">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1000"
              inputMode="decimal"
              placeholder="0.00"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              onBlur={onCustomBlur}
              className="input pl-7"
              data-testid="tip-custom-input"
            />
          </div>
          <p className="text-[10px] text-[#B9C8D5] mt-1">
            Press Tab or click outside the box to apply the tip.
          </p>
        </div>
      )}

      {currentTip > 0 && (
        <div className="mt-3 text-xs text-emerald-300" data-testid="tip-current-total">
          Tip added: <span className="font-medium">${currentTip.toFixed(2)}</span>
        </div>
      )}
    </section>
  );
};

export default TipSection;
